import React, { useEffect, useState } from 'react';
import { useStore } from 'react-redux';
import Company from '../../../fields/company';
import Rider from '../../../fields/rider';
import Area from '../../../fields/area';
import Status from '../../../fields/status';
import TextField from '@mui/material/TextField';
import Button from '@material-ui/core/Button';
import API from '../../../create/api';
import * as helper from '../../../../helper/index';
import { useParams, useHistory } from 'react-router-dom';
import Switch from '@mui/material/Switch';
import moment from 'moment-timezone';
import Comment from './components/comment';
import { toastr } from 'react-redux-toastr';
import LoadingButton from '@mui/lab/LoadingButton';
import DatePickerComponent from '../../../../ui/Datepicker';
import { updateLastCompanyAndClearParcelIds } from '../../../../store/thunk-actions';
import { type AnyAction } from 'redux';
import TimelineComponent from './components/Timeline';
import _ from 'lodash';
import PhoneNumberInput from './components/PhoneNumberInput';
import { startCase } from 'lodash';
import { Box } from '@mui/material';

export default function BookParcel(props: any) {
  // State and hooks initialization
  const store = useStore();
  const history = useHistory();
  const { parcel_id } = props;
  const [load, setLoad]: any = React.useState(false);
  const { id: id_param }: any = useParams();
  const [id, setID]: any = React.useState(id_param ?? parcel_id);
  const [getRequestBody, setRequestBody]: any = React.useState({ addToBalance: false });
  const [get_log, set_log]: any = React.useState([]);
  const [status, setStatus]: any = React.useState({
    label: 'ON_DELIVERY',
    value: 'ON_DELIVERY',
  });
  const [memoNo, setMemoNo]: any = React.useState('');
  const [customerName, setCustomerName]: any = React.useState('');
  const [phoneNumber, setPhoneNumber]: [string, any] = React.useState('');
  const [address, setAddress]: any = React.useState('');
  const [weight, setWeight]: any = React.useState(1);
  const [COD, setCOD]: any = React.useState(0);
  const [get_collected_amount, set_collected_amount]: any = React.useState(0);
  const [get_return_product_price, set_return_product_price]: any = React.useState(null);
  const [deliveryCharge, setDeliveryCharge]: any = React.useState(
    store.getState().parcel?.last_delivery_charge ?? undefined,
  );
  const [details, setDetails]: any = React.useState('');
  const [booking_date, set_booking_date]: any = React.useState(moment().tz('Asia/Dhaka').format('YYYY-MM-DD'));
  const [get_payment_date, set_payment_date]: any = React.useState(null);
  const [company, setCompany] = React.useState(store.getState().parcel?.last_company);
  const [rider, setRider]: any = React.useState(null);
  const [area, setArea]: any = React.useState(null);
  const [get_parcel, set_parcel]: any = React.useState(null);
  const [get_loading, set_loading]: any = React.useState(false);

  // Function to set parcel data
  function setParcelData(parcel: any) {
    const {
      id,
      parcel_logs,
      area,
      memoNo,
      customerName,
      phoneNumber,
      rider,
      merchant_COD,
      address,
      details,
      status,
      company,
      deliveryCharge,
      weight,
      booking_date,
      collected_amount,
      return_product_price,
      payment_date,
    } = parcel;
    setRequestBody(_.pick({ ...getRequestBody, ...parcel }, ['addToBalance', 'returnToMerchant']));
    set_payment_date(payment_date);
    set_return_product_price(return_product_price);
    set_collected_amount(collected_amount);
    booking_date && set_booking_date(booking_date);
    setWeight(weight);
    company?.id && setCompany({ label: company?.name, value: company?.id });
    setStatus({ label: status, value: status });
    set_parcel(parcel);
    setDetails(details);
    setAddress(address);
    setCOD(merchant_COD);
    setDeliveryCharge(deliveryCharge);
    rider?.id && setRider({ label: `${rider.id}: ${startCase(rider.name.trim())}`, value: rider.id });
    setPhoneNumber(phoneNumber);
    setCustomerName(customerName);
    setMemoNo(memoNo);
    area?.id && setArea({ label: area.name, value: area.id });
    set_log([...(parcel_logs ?? [])]);
    setID(id);
    setLoad(true);
  }

  // Effect to fetch parcel data
  React.useEffect(() => {
    if (id) {
      API.get(`${process.env.REACT_APP_API_SERVER}/parcels`, id)
        .then((parcel) => {
          setParcelData(parcel);
        })
        .catch((e) => {
          helper.axiosErrorHandling(e);
        });
    } else {
      setLoad(true);
    }
  }, [id]);

  // Function to create or update parcel
  async function create() {
    set_loading(true);
    const data: Record<string, any> = {
      ...getRequestBody,
      status: status?.value,
      memoNo,
      customerName,
      company: company?.value ?? null,
      rider: rider?.value ?? null,
      area: area?.value ?? null,
      phoneNumber,
      address,
      weight,
      merchant_COD: COD,
      deliveryCharge,
      details,
      booking_date,
      collected_amount: get_collected_amount,
      return_product_price: get_return_product_price,
      payment_date: get_payment_date,
    };
    if (!deliveryCharge) {
      toastr.warning('Are you sure?', 'Delivery charge is ZERO');
    }

    if (
      (status?.value === 'RETURNED' || status?.value === 'PARTIALLY_DELIVERED') &&
      (get_return_product_price === 0 || get_return_product_price === null)
    ) {
      toastr.warning('Are you sure?', 'Return Product Price is ZERO');
    }

    // Remove null fields
    Object.keys(data).forEach((key: string) => {
      if (data[key] === null) {
        delete data[key];
      }
    });

    if (status?.value === 'ON_DELIVERY' && !rider?.value) {
      set_loading(false);
      toastr.warning('Empty', 'Rider cannot be empty when status is ON_DELIVERY');
      return;
    }

    const url = `${process.env.REACT_APP_API_SERVER}/parcels`;
    if (id) {
      await API.update(url, id, data)
        .then((parcel) => {
          setParcelData(parcel);
          toastr.success('Updated', '');
        })
        .catch((e) => {
          helper.axiosErrorHandling(e);
        })
        .finally(() => {
          set_loading(false);
        });
    } else {
      await API.create(url, data)
        .then((parcel: any) => {
          toastr.success('Created', '');
          history.push(`/dashboard/parcel/edit/${parcel.id}`);
        })
        .catch((e) => {
          helper.axiosErrorHandling(e);
        })
        .finally(() => {
          set_loading(false);
        });
    }
  }

  // JSX for rendering the component
  return (
    load && (
      <div className="flex flex-wrap -mx-4">
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <DatePickerComponent
              label="Booking Date"
              value={booking_date}
              onChange={(newValue: string | null) => set_booking_date(newValue)}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <Status
              onChange={(cp: any) => {
                setStatus(cp ?? null);
                if (cp?.label === 'RETURNED') {
                  set_return_product_price(0);
                  set_collected_amount(0);
                }
              }}
              defaultValue={status}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <Company
              onChange={(cp: any) => {
                setCompany(cp ?? null);
                store.dispatch(updateLastCompanyAndClearParcelIds(cp) as unknown as AnyAction);
              }}
              defaultValue={id ? company : store.getState().parcel?.last_company}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <TextField
              variant="standard"
              fullWidth
              label="Invoice*"
              onChange={(e) => {
                setMemoNo(e.target.value);
              }}
              value={memoNo ?? ''}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <PhoneNumberInput
              phoneNumber={phoneNumber}
              setPhoneNumber={setPhoneNumber}
              setCustomerName={setCustomerName}
              setAddress={setAddress}
              setRider={(riderId: any) => {
                if (riderId) {
                  API.get(`${process.env.REACT_APP_API_SERVER}/riders`, riderId)
                    .then((rider: any) => {
                      if (rider?.id) {
                        setRider({ label: `${rider.id}: ${startCase(rider.name.trim())}`, value: rider.id });
                      }
                    })
                    .catch((e) => {
                      helper.axiosErrorHandling(e);
                    });
                } else {
                  setRider(null);
                }
              }}
              setArea={(areaId: any) => {
                if (areaId) {
                  API.get(`${process.env.REACT_APP_API_SERVER}/areas`, areaId)
                    .then((area: any) => {
                      if (area?.id) {
                        setArea({ label: area.name, value: area.id });
                      }
                    })
                    .catch((e) => {
                      helper.axiosErrorHandling(e);
                    });
                } else {
                  setArea(null);
                }
              }}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <TextField
              variant="standard"
              label="Customer Name*"
              onChange={(e) => {
                setCustomerName(e.target.value);
              }}
              value={customerName ?? ''}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <TextField
              variant="standard"
              fullWidth
              label="Address*"
              onChange={(e) => {
                setAddress(e.target.value);
              }}
              value={address ?? ''}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <Area onChange={(cp: any) => setArea(cp ?? null)} defaultValue={area} />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <Rider onChange={(cp: any) => setRider(cp ?? null)} defaultValue={rider} />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <TextField
              type="number"
              variant="standard"
              fullWidth
              label="Weight Kg*"
              onChange={(e) => {
                setWeight(e.target.value);
              }}
              value={weight}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <TextField
              variant="standard"
              fullWidth
              label="Merchant COD"
              onChange={(e) => {
                setCOD(e.target.value);
              }}
              value={COD}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <TextField
              variant="standard"
              fullWidth
              label="Delivery Charge"
              value={deliveryCharge}
              onChange={(e) => {
                store.dispatch({
                  type: 'UPDATE_LAST_DELIVERY_CHARGE',
                  payload: Number(e.target.value) ?? undefined,
                });
                setDeliveryCharge(e.target.value ?? undefined);
              }}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <TextField
              type="number"
              variant="standard"
              fullWidth
              label="Collected Amount"
              onChange={(e) => {
                set_collected_amount(Number(e.target.value) ?? 0);
                if (status?.value === 'PARTIALLY_DELIVERED' && COD) {
                  set_return_product_price(COD - Number(e.target.value) ?? 0);
                }
              }}
              value={get_collected_amount}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <TextField
              type="number"
              variant="standard"
              fullWidth
              label="Return Product Price"
              // AG-10
              disabled={!(status?.value === 'RETURNED' || status?.value === 'PARTIALLY_DELIVERED')}
              onChange={(e) => {
                set_return_product_price(Number(e.target.value) ?? 0);
                if (status?.value === 'PARTIALLY_DELIVERED' && COD) {
                  set_collected_amount(COD - Number(e.target.value) ?? 0);
                }
              }}
              value={get_return_product_price ?? 0}
            />
          </div>
        </div>

        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <TextField
              variant="standard"
              fullWidth
              label="Details by Merchant"
              onChange={(e) => {
                setDetails(e.target.value);
              }}
              value={details ?? ''}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700">
            <DatePickerComponent
              label="Payment Date"
              value={get_payment_date}
              onChange={(newValue: string | null) => {
                set_payment_date(newValue);
              }}
            />
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg text-center text-gray-700">
            <div className="p-4 text-center text-gray-600">
              Add To Balance:
              <Switch
                checked={getRequestBody.addToBalance ?? false}
                onChange={(checked) => {
                  setRequestBody({ ...getRequestBody, addToBalance: checked.target.checked });
                }}
                name="loading"
                color="primary"
              />
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg text-center text-gray-700">
            <div className="p-4 text-center text-gray-600">
              Parcel Returned
              <Switch
                checked={getRequestBody.returnToMerchant ?? false}
                onChange={(checked) => {
                  setRequestBody({ ...getRequestBody, returnToMerchant: checked.target.checked });
                }}
                name="loading"
                color="primary"
              />
            </div>
          </div>
        </div>
        <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
          <div className="bg-white shadow-md rounded-lg p-4 text-center text-gray-700">
            {get_loading ? (
              <LoadingButton loading loadingPosition="start" variant="outlined">
                {id > 0 ? 'Updating..' : 'Saving..'}
              </LoadingButton>
            ) : (
              <Button variant="contained" color="primary" onClick={create}>
                {id > 0 ? 'Update' : 'Save'}
              </Button>
            )}
          </div>
        </div>
        {id && (
          <div className="w-full md:w-1/3 lg:w-1/4 px-2 mb-6">
            <div className="bg-white shadow-md rounded-lg text-center text-gray-700">
              <div className="p-4 text-center text-gray-600">
                ID: <b>{id}</b>
                <br />
                Updated:
                {get_parcel?.updated_at && moment(get_parcel.updated_at).format('DD-MMM-YYYY hh:mm a')}
              </div>
            </div>
          </div>
        )}

        {id && (
          <div className="flex justify-center w-full px-4 mb-6">
            <div className="bg-white shadow-md rounded-lg p-6 text-center text-gray-700 w-full md:w-2/3 lg:w-1/2">
              <div className="p-4 text-center text-gray-600">
                <TimelineComponent get_log={get_log} />
              </div>
              <div className="p-4 text-center text-gray-600">
                <Comment
                  parcel_id={id}
                  reload_parcel={async () => {
                    await API.get(`${process.env.REACT_APP_API_SERVER}/parcels`, id)
                      .then((parcel) => {
                        setParcelData(parcel);
                      })
                      .catch((e) => {
                        helper.axiosErrorHandling(e);
                      });
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    )
  );
}

// some documentation
// if status is ON_DELIVERY then rider cannot be empty
// if status is RETURNED then return_product_price set to 0
// if status is PARTIALLY_DELIVERED then collected_amount and return_product_price should be set
